/**
 * Hook to fetch waveform peaks JSON from CDN for audio assets.
 *
 * Bridges derived-asset completion → JSON fetch → WaveformData.
 * Enabled only when a complete waveform_v1 derived asset exists
 * and CloudFront auth is available.
 */

import { useQuery } from "@tanstack/react-query";
import { useDerivedAssets } from "./useDerivedAssets";
import { useSignedCookies } from "./useSignedCookies";
import { buildCloudFrontAssetUrl } from "@/components/timeline/media/ChunkedVideoManager";
import type { WaveformData } from "@/components/timeline/types";

interface WaveformJson {
  peaks?: number[];
  peaksLeft?: number[];
  peaksRight?: number[];
  duration: number;
  sampleRate: number;
  channels: number;
  bitDepth: number;
  sourceSampleRate: number;
  windowSize: number;
  peakMethod: string;
  version: number;
}

/**
 * Fetch and parse waveform peaks JSON for a single asset.
 *
 * @param assetId - The source asset ID
 * @param projectId - Project ID for CDN URL resolution
 * @returns Query result with WaveformData (including clipId placeholder)
 */
export function useWaveformPeaksLoader(
  assetId: string | undefined,
  projectId: string | undefined,
) {
  const { getWaveform } = useDerivedAssets(assetId);
  const waveformDerived = getWaveform();
  const { data: auth } = useSignedCookies(projectId);

  return useQuery({
    queryKey: ["waveform-peaks", assetId],
    queryFn: async (): Promise<WaveformJson> => {
      // Build clean CloudFront URL (cookies provide auth)
      const url = buildCloudFrontAssetUrl(
        projectId!,
        assetId!,
        "waveform.json",
        {},
        undefined,
        auth!.domain,
      );
      // Include credentials to send CloudFront cookies
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        throw new Error(`Waveform fetch failed: ${resp.status}`);
      }
      return (await resp.json()) as WaveformJson;
    },
    enabled: !!waveformDerived && !!auth && !!assetId && !!projectId,
    staleTime: Infinity,
  });
}

/**
 * Convert fetched waveform JSON to WaveformData for a specific clip.
 */
export function toWaveformData(
  json: WaveformJson,
  clipId: string,
): WaveformData {
  return {
    clipId,
    peaks: json.peaks,
    peaksLeft: json.peaksLeft,
    peaksRight: json.peaksRight,
    duration: json.duration,
    sampleRate: json.sampleRate,
    channels: json.channels,
    bitDepth: json.bitDepth,
    sourceSampleRate: json.sourceSampleRate,
    windowSize: json.windowSize,
    peakMethod: json.peakMethod,
    version: json.version,
  };
}
